import type { Size, Vec2, Bounds } from '@/types';
import type { DefaultPosition } from '@/applications/types';

export const CASCADE_STEP = 32;

/**
 * Where to put a new window, in world units. `visible` is the current viewport in
 * world space; `existing` are top-left corners of other windows (for cascading).
 * Pure so placement is unit-testable and independent of the camera store.
 */
export const placeWindow = (
  preference: DefaultPosition,
  size: Size,
  visible: Bounds,
  existing: readonly Vec2[],
): Vec2 => {
  if (typeof preference === 'object') return preference;
  const cx = (visible.minX + visible.maxX) / 2 - size.width / 2;
  const cy = (visible.minY + visible.maxY) / 2 - size.height / 2;
  if (preference === 'center' || existing.length === 0) return { x: Math.round(cx), y: Math.round(cy) };
  // Cascade from the most recent window that is still in view; wrap when it would leave the viewport.
  const last = existing[existing.length - 1] ?? { x: cx, y: cy };
  const next = { x: last.x + CASCADE_STEP, y: last.y + CASCADE_STEP };
  const fits = next.x + size.width <= visible.maxX && next.y + size.height <= visible.maxY;
  return fits ? next : { x: Math.round(visible.minX + CASCADE_STEP), y: Math.round(visible.minY + CASCADE_STEP) };
};
